import { apiClient } from './client';
import type { LoginRequest, LoginResponse, User } from '@/types';

export interface ProductAccessResponse {
  products: string[];
  all_access: boolean;
}

export interface UnlockProductResponse {
  success: boolean;
  products: string[];
  product: string;
  message: string;
}

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', {
      email: credentials.email,
      password: credentials.password,
    });
    return response.data;
  },

  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  refreshToken: async (refreshToken: string): Promise<{ access_token: string }> => {
    const response = await apiClient.post('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  // Product access management
  getProductAccess: async (): Promise<ProductAccessResponse> => {
    const response = await apiClient.get<ProductAccessResponse>('/auth/products/access');
    return response.data;
  },

  unlockProduct: async (accessCode: string): Promise<UnlockProductResponse> => {
    const response = await apiClient.post<UnlockProductResponse>('/auth/products/unlock', {
      access_code: accessCode,
    });
    return response.data;
  },

  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post('/auth/forgot-password', { email });
  },

  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/reset-password', { token, new_password: newPassword });
  },

  verifyEmail: async (token: string): Promise<void> => {
    await apiClient.post('/auth/verify-email', { token });
  },

  ssoExchange: async (code: string): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/sso/exchange', { code });
    return response.data;
  },

  getMe: async (): Promise<MeResponse> => {
    const response = await apiClient.get<MeResponse>('/auth/me');
    return response.data;
  },
};

// /auth/me response (includes products)
export interface MeResponse {
  user_id: string;
  client_id: string;
  email: string;
  full_name: string;
  role: string;
  client_name: string | null;
  permissions: Record<string, unknown>;
  products: string[];
}

// ─── Invite API ───────────────────────────────────────────────────────────

export interface InviteRecord {
  id: string;
  email: string;
  role: string;
  products: string[];
  status: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export interface AcceptInviteResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    full_name: string;
    role: string;
    client_id: string;
    client_name: string | null;
    products: string[];
  };
}

export const inviteApi = {
  create: async (email: string, role: string, products: string[]): Promise<InviteRecord> => {
    const response = await apiClient.post<InviteRecord>('/invites', { email, role, products });
    return response.data;
  },

  list: async (): Promise<InviteRecord[]> => {
    const response = await apiClient.get<InviteRecord[]>('/invites');
    return response.data;
  },

  revoke: async (id: string): Promise<void> => {
    await apiClient.delete(`/invites/${id}`);
  },

  accept: async (token: string, fullName: string, password: string): Promise<AcceptInviteResponse> => {
    const response = await apiClient.post<AcceptInviteResponse>('/invites/accept', {
      token,
      full_name: fullName,
      password,
    });
    return response.data;
  },
};

// ─── Team API ─────────────────────────────────────────────────────────────

export interface TeamMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
  products: string[];
  token_allocation: number | null;  // per-seat token cap (null = unlimited / shared pool)
  token_used: number;               // lifetime tokens this member has consumed
  is_active: boolean;
  last_login: string | null;
  created_at: string | null;
}

export const teamApi = {
  list: async (): Promise<TeamMember[]> => {
    const response = await apiClient.get<TeamMember[]>('/team');
    return response.data;
  },

  updateRole: async (userId: string, role: string): Promise<void> => {
    await apiClient.patch(`/team/${userId}/role`, { role });
  },

  updateProducts: async (userId: string, products: string[]): Promise<void> => {
    await apiClient.patch(`/team/${userId}/products`, { products });
  },

  updateAllocation: async (userId: string, tokens: number | null): Promise<void> => {
    await apiClient.patch(`/team/${userId}/allocation`, { tokens });
  },

  deactivate: async (userId: string): Promise<void> => {
    await apiClient.delete(`/team/${userId}`);
  },
};
